import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ComponentType } from '@angular/cdk/portal';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';

import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { FilterDialogComponent } from '../filter-dialog/filter-dialog.component';
import { UIListFilters } from './filters';

/**
 * This component is used to manage a list of filters in a desktop and mobile UI.
 *
 * In order to make it work properly, the concrete filters transcluded need to follow some rules.
 *
 * 1. Implement `UIListFilters` interface in the component that contains the filters;
 * 2. Declare an HTML variable as showed below in this component:
 *
 * ```HTML
 * <ui-filters [dialogComponent]="FiltersDialogComponent">
 *   <app-concrete-filters
 *      #desktopFilter
 *      (apply)="applyFilterWithDelayHandler($event)">
 *    </app-concrete-filters>
 * </ui-filters>
 * ```
 *
 * The `desktopFilter` is used to query `ng-content` in order to use `apply` EventEmmitter implemented from the interface `UIListFilters`
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  // tslint:disable-next-line:component-selector
  selector: 'ui-filters',
  styleUrls: ['./filters.component.scss'],
  templateUrl: './filters.component.html',
})
export class FiltersComponent<T> implements AfterContentInit, OnDestroy {
  @Input() displayMode: 'row' | 'column' = 'row';
  @Input() dialogFilters?: ComponentType<T>;

  @Input() showFiltersTitle = true;

  @Output() private readonly action = new EventEmitter<T>();

  @ContentChild('desktopFilter') private readonly desktopFilter?: ComponentType<T> & UIListFilters<T>;

  readonly state$ = new BehaviorSubject<{ config?: MatDialogConfig }>({
    config: undefined,
  });
  readonly streamState$ = this.state$.asObservable();

  get state() {
    return this.state$.value;
  }

  private readonly unsubscription$ = new Subject();

  constructor(
    private readonly dialog: MatDialog,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
    // Removed breakpoint observer logic since filters are now permanently visible
  }

  ngAfterContentInit() {
    if (this.desktopFilter && this.desktopFilter.apply) {
      this.desktopFilter.apply
        .pipe(debounceTime(1000))
        .subscribe((data) => this.action.emit(data));
    } else {
      throw new Error('Cannot use this UI component without implementig related interfaces as documented');
    }
  }

  ngOnDestroy() {
    this.unsubscription$.next(null);
    this.unsubscription$.complete();
  }

  // Removed showFiltersHandler since filters are now permanently visible

  private setState(state: Partial<{ config: MatDialogConfig }>) {
    this.state$.next({ ...this.state, ...state });
  }
}
